import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSite } from '../site/SiteProvider';
import { useLector } from '../config/lector';
import { buildReach } from '../config/alcance';
import { fetchCatalog, fetchTeachings, fetchTeaching, fetchPiece, fetchPreview, fetchDaily, fetchSearch, fetchDoors, fetchThirdParty, localDate } from './contentApi';
import { useReadTracking } from './readTracking';

const useContentContext = () => {
  const { site } = useSite();
  const { i18n } = useTranslation();
  const { token, planName, ready } = useLector();
  return { site, lang: i18n.language, token, plan: planName, ready };
};

const useRemote = (load, dependencies, enabled) => {
  const [state, setState] = useState({ data: null, loading: true, error: null });

  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    setState((previous) => ({ ...previous, loading: true, error: null }));
    load()
      .then((data) => { if (alive) setState({ data, loading: false, error: null }); })
      .catch((error) => {
        console.error(error);
        if (alive) setState({ data: null, loading: false, error: error.message });
      });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, ...dependencies]);

  return state;
};

export const useCatalog = () => {
  const context = useContentContext();
  const { data, loading, error } = useRemote(
    () => fetchCatalog(context),
    [context.site, context.lang, context.plan],
    context.ready
  );
  return { items: data || EMPTY_LIST, loading: loading || !context.ready, error };
};

export const useTeachings = () => {
  const context = useContentContext();
  const { data, loading, error } = useRemote(
    () => fetchTeachings(context),
    [context.site, context.lang, context.plan],
    context.ready
  );
  return { items: data || EMPTY_LIST, loading: loading || !context.ready, error };
};

export const useTeaching = (number) => {
  const context = useContentContext();
  const { data, loading, error } = useRemote(
    () => fetchTeaching({ ...context, number }),
    [context.site, context.lang, context.plan, number],
    context.ready && Number.isInteger(number)
  );
  useReadTracking(data, context);
  return { teaching: data, loading: loading || !context.ready, error, notFound: !loading && !error && data === null };
};

export const usePiece = (slug) => {
  const context = useContentContext();
  const { data, loading, error } = useRemote(
    () => fetchPiece({ ...context, slug, date: localDate() }),
    [context.site, context.lang, context.plan, slug],
    context.ready && Boolean(slug)
  );
  useReadTracking(data, context);
  return { piece: data, loading: loading || !context.ready, error, notFound: !loading && !error && data === null };
};

export const usePreviewPiece = (previewToken) => {
  const { i18n } = useTranslation();
  const { data, loading, error } = useRemote(
    () => fetchPreview({ lang: i18n.language, token: previewToken }),
    [i18n.language, previewToken],
    Boolean(previewToken)
  );
  return { piece: data, loading: Boolean(previewToken) && loading, error };
};

export const useDailyPiece = () => {
  const context = useContentContext();
  const { data, loading, error } = useRemote(
    () => fetchDaily({ ...context, date: localDate() }),
    [context.site, context.lang, context.plan],
    context.ready
  );
  return { daily: data, loading: loading || !context.ready, error };
};

export const useDoors = () => {
  const context = useContentContext();
  const { data, loading } = useRemote(
    () => fetchDoors(context),
    [context.site, context.lang, context.plan],
    context.ready
  );
  return { doors: data || EMPTY_LIST, loading: loading || !context.ready };
};

export const useSearch = (text) => {
  const context = useContentContext();
  const query = String(text || '').trim();
  const { data, loading, error } = useRemote(
    () => fetchSearch({ ...context, q: query }),
    [context.site, context.lang, context.plan, query],
    context.ready && query.length > 0
  );
  return { items: query && data ? data : EMPTY_LIST, loading: Boolean(query) && loading, error };
};

export const useThirdParty = (page, pageSize, enabled) => {
  const context = useContentContext();
  const { data, loading, error } = useRemote(
    () => fetchThirdParty({ ...context, page, pageSize }),
    [context.site, context.lang, context.plan, page, pageSize],
    context.ready && enabled
  );
  return { items: data ? data.items : EMPTY_LIST, total: data ? data.total : 0, loading: loading || !context.ready, error };
};

export const useReach = () => {
  const { stats } = useSite();
  return buildReach(stats);
};

const EMPTY_LIST = [];
